import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import settingsParams from '../../components/BookOnline/settingsParams';
import { FilterOption, FilterServicesByOptions } from '../../types/types';
import { BookingsAPI } from '../../api/BookingsApi';
import {
  BookingsQueryParams,
  getUrlQueryParamValue,
} from '@wix/bookings-catalog-calendar-viewer-utils';
import {
  ALL_SERVICES,
  REQUESTED_CATEGORIES_URL_PARAM_NAME,
} from '../../consts';
import { ReservedLocationIds } from '@wix/bookings-uou-types';
import { ServiceListSettings } from '../../../legacy/appSettings/appSettings';
import {
  LocationType,
  V2Category,
} from '@wix/ambassador-bookings-services-v2-service/build/cjs/types.impl';

export async function getFilterOptions({
  flowAPI,
  bookingsApi,
  appSettings,
}: {
  flowAPI: ControllerFlowAPI;
  bookingsApi: BookingsAPI;
  appSettings?: ServiceListSettings;
}): Promise<FilterOption[] | undefined> {
  const {
    settings,
    translations: { t },
    experiments,
  } = flowAPI;

  if (
    appSettings
      ? !appSettings.DISPLAY_CATEGORIES
      : !settings.get(settingsParams.isListFilterVisible)
  ) {
    return undefined;
  }

  const filterServicesBy = appSettings
    ? appSettings.CATEGORIES_TYPE
    : settings.get(settingsParams.filterServicesBy);

  const filterOptions =
    filterServicesBy === FilterServicesByOptions.CATEGORIES
      ? await bookingsApi
          .fetchCategories()
          .then((unsortedCategories) =>
            unsortedCategories?.sort(sortCategories).map((category) => ({
              id: category.id,
              title: category.name,
              isSelected: false,
            })),
          )
          .catch((error) => {
            console.error(error);
            return undefined;
          })
      : await bookingsApi
          .fetchLocationTabs()
          .then(({ locationTypes, businessLocations }) => {
            const locations =
              businessLocations?.sort(sortLocations).map((location) => ({
                id: location.id,
                title: location.name,
                isSelected: false,
              })) ?? [];
            const hasOtherLocations = locationTypes?.find(
              (locationType) => locationType !== LocationType.BUSINESS,
            );

            return hasOtherLocations
              ? [
                  ...locations,
                  {
                    id: ReservedLocationIds.OTHER_LOCATIONS,
                    title: t(
                      'service-list.categories.location-categories.other-locations',
                    ),
                    isSelected: false,
                  },
                ]
              : locations;
          })
          .catch((error) => {
            console.error(error);
            return undefined;
          });

  const showAllServicesFilterOption =
    ((appSettings
      ? appSettings.CATEGORY_ALL_SERVICES_SHOW
      : settings.get(settingsParams.showAllServicesFilterOption)) ||
      experiments.enabled(
        'specs.bookings.disableShowAllServicesFilterOption',
      )) &&
    filterOptions &&
    filterOptions.length > 1;

  if (showAllServicesFilterOption) {
    filterOptions.unshift({
      id: ALL_SERVICES,
      title:
        (appSettings
          ? (appSettings.CATEGORY_ALL_SERVICES_TEXT as string)
          : settings.get(settingsParams.allServices)) ||
        t('category-name.all-services'),
      isSelected: true,
    });
  } else if (filterOptions && filterOptions.length > 0) {
    filterOptions[0].isSelected = true;
  }

  const preSelectedCategory = getUrlQueryParamValue(
    flowAPI.controllerConfig.wixCodeApi,
    REQUESTED_CATEGORIES_URL_PARAM_NAME as BookingsQueryParams,
  );

  const hasCategoryDeepLink = !!preSelectedCategory;

  const preSelectedLocation = getUrlQueryParamValue(
    flowAPI.controllerConfig.wixCodeApi,
    BookingsQueryParams.LOCATION,
  );

  const hasLocationDeepLink = !!preSelectedLocation;

  if (
    hasCategoryDeepLink &&
    filterServicesBy === FilterServicesByOptions.CATEGORIES
  ) {
    choosePreselectedFilter(filterOptions, preSelectedCategory);
  } else if (
    hasLocationDeepLink &&
    filterServicesBy === FilterServicesByOptions.LOCATIONS
  ) {
    choosePreselectedFilter(filterOptions, preSelectedLocation);
  }

  return filterOptions;
}

const choosePreselectedFilter = (
  filterOptions: FilterOption[] | undefined,
  preSelectedFilterId: string,
) => {
  filterOptions?.forEach((filterOption) => {
    filterOption.isSelected = filterOption.id === preSelectedFilterId;
  });
  if (
    filterOptions?.every((filterOption) => !filterOption.isSelected) &&
    filterOptions.length > 0
  ) {
    filterOptions[0].isSelected = true;
  }
};

const sortLocations = (
  location1: { name: string; id: string },
  location2: { name: string; id: string },
) => {
  if (location1.id === ReservedLocationIds.OTHER_LOCATIONS) {
    return 1;
  }
  if (location2.id === ReservedLocationIds.OTHER_LOCATIONS) {
    return -1;
  }
  return location1.name > location2.name ? 1 : -1;
};

const sortCategories = (category1: V2Category, category2: V2Category) =>
  category1?.sortOrder! - category2?.sortOrder!;
